import { bookingStore } from "entities/booking";
import { clinicsStore } from "entities/clinics";
import { TimeslotMonth, timeslotsStore } from "entities/timeslots";
import { DoctorCard, doctorsStore } from "features/doctors";
import { IDay, ScheduleCalendar, ScheduleFeedHeader } from "features/schedule";
import { observer } from "mobx-react-lite";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { formatWithLocale, mappingDaysToMonths } from "shared/lib/date";
import { SectionWithActionButton } from "shared/ui/sectionWithActionButton";
import hospitalIcon from 'assets/hospital_medical.svg';
import { Specialization } from "entities/specializations";
import { Spinner } from "shared/ui/preloader/spinner/spinner";
import clinicIcon from 'assets/iconfinder_geo.svg';
import styles from './styles.module.scss';
import { ScheduleFeedMonth } from "features/schedule/ui/scheduleFeed/scheduleFeedMonth/scheduleFeedMonth";
import { ITimeslot } from "entities/timeslots/store/types";
import { Doctor as TDoctor } from "features/doctors/store/types";
import { SuccessModal } from "features/appointment";
import { UNAVAILABLE_TEXT } from "shared/consts";

export const Doctor: FC = observer(() => {
  const { id, view } = useParams();
  const navigate = useNavigate();

  const bookingDateRef = useRef<string>('');

  const [days, setDays] = useState<IDay[]>([]);
  
  const [ isLoading, setIsLoading ] = useState<boolean>(true);
	const [ selectedDay, setSelectedDay ] = useState<IDay | null>(null);
  const [ specialization, setSpecialization ] = useState<Specialization | null>(null)
	const [calendarIsOpen, setCalendarOpened] = useState<boolean>(false);
  const [timeslot, setTimeslot] = useState<ITimeslot | null>(null);
  const [ isErrorModalOpen, setIsErrorModalOpen ] = useState<boolean>(false);
  
  const fetchBookingDataAndSetByDate = useCallback(async (date: string) => {
    bookingDateRef.current = date;
    bookingStore.setDate(date);
    await timeslotsStore.fetchSlotsByDoctors(date, id);
  }, [id, bookingDateRef])
  
  const getActiveDay = (days?: TimeslotMonth[]) => {
    if (!days) return null;

    const dateFromLocalStorage = localStorage.getItem('date');
    const now = formatWithLocale(new Date(), 'yyyy-MM-dd');

    if (dateFromLocalStorage) {
      const finded = days.find(el => el.date === dateFromLocalStorage);
      if (finded&&finded.has_slots) return dateFromLocalStorage;
    };
    
    const nowFinded = days.find(el => el.date === now);
    if (nowFinded&&nowFinded.has_slots) return now;

    const first = days.find(el => el.has_slots === true);

    return first?.date;
  }

  const getInfo = async () => {
    if (!id) {
      return setTimeout(() => {
        getInfo()
      }, 500);
    }
    setIsLoading(true);

    const activeDoctor = await doctorsStore.fetchDoctorById(id);
    if (!activeDoctor) {
      return;
    }

    if (activeDoctor.isError) {
      setIsErrorModalOpen(true)
      return;
    }

    bookingStore.setSpecialization(activeDoctor.specializations[0])
    setSpecialization(activeDoctor.specializations[0])

    await clinicsStore.fetchClinics(activeDoctor.specializations[0].id);
    const localClinics = localStorage.getItem('clinics');
    if (localClinics) {
      bookingStore.setClinics(JSON.parse(localClinics))
    } else {
      bookingStore.setClinics([])
    }

    const monthsRes = await timeslotsStore.fetchMonths(id);
    
		const bookingDate = getActiveDay(monthsRes);
    if (bookingDate) {
      await fetchBookingDataAndSetByDate(bookingDate)
    }

    if (monthsRes) {
      setDays(
        monthsRes.map(month => {
          const obj = { ...month, isActive: false };

          if (month.date === bookingDate) {
            obj.isActive = true;
            setSelectedDay(obj);
          }

          return obj;
        }),
      );
    }

    setIsLoading(false);
  }

  const redirect = (url: string, setRedirect: boolean = true) => () => {
    if (setRedirect) {
      const redirectFromDoctorPage = location.pathname
      bookingStore.setRedirectFromDoctorPage(redirectFromDoctorPage)
    } else {
      bookingStore.setRedirectFromDoctorPage(null)
    }
    navigate(url);
  };

	const handleOpenCalendar = () => {
		setCalendarOpened(!calendarIsOpen);
	};

  // eslint-disable-next-line
  const selectDay = (day: IDay) => {
    setSelectedDay(day);
  };

  const changeDayActive = useCallback(
    async (date: string) => {
      if (date) {
        bookingDateRef.current = date;
        fetchBookingDataAndSetByDate(date);
        const cloneDays = Array.from(days);
        cloneDays.map((day, index) => {
          if (day.date === date) {
            const dayObj = { ...day, isActive: true };
            cloneDays[index] = dayObj;
            selectDay(dayObj);
          } else {
            cloneDays[index] = { ...day, isActive: false };
          }
        });

        setDays(cloneDays);
      }
    },
    [setDays, selectDay, days, fetchBookingDataAndSetByDate],
  );

	const closeCalenar = () => {
		setCalendarOpened(false);
	};

	const selectSlot = useCallback((slot: ITimeslot) => {
		setTimeslot(slot);
	}, []);

  useEffect(() => {
    getInfo()
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (bookingStore.date !== bookingDateRef.current) {
      changeDayActive(bookingStore.date);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingStore.date, changeDayActive]);
  
  const months = useMemo(() => {
    return mappingDaysToMonths(days);
  }, [days])

  if (isLoading) {
    return (
      <div className="flex align-center justify-center h-screen">
        <Spinner />
        <SuccessModal 
          isOpen={isErrorModalOpen}
          // title=""
          text={UNAVAILABLE_TEXT}
          // buttonText=""
          onClick={() => navigate('/', {replace: true})}
        />
      </div>
    )
  }

  return (
    <>
      {view === 'full' && 
        <>
          <div className="mb-2 mt-4">
            <SectionWithActionButton
              title="Специализация"
              subtitle={[specialization?.name || '']}
              buttonText="Изменить"
              icon={{ src: hospitalIcon }}
              onClick={redirect('/specializations', false)}
            />
          </div>
          <div className="mb-2">
            <SectionWithActionButton
              title="Клиники"
              subtitle={bookingStore.getClinicsNames()}
              placeholder="Все клиники"
              buttonText="Изменить"
              icon={{ src: clinicIcon, size: '25px' }}
              onClick={redirect('/clinics')}
            />
          </div>
        </>
      }

      <div className={view !== 'full' ? ' mt-4' : ''}>
        <ScheduleFeedHeader
          selectedDay={selectedDay}
          setOpenCalendar={handleOpenCalendar}
          isOpenCalendar={calendarIsOpen}
        />
      </div>
      
      {!calendarIsOpen && 
      <div className={styles['schedule-feed-list']}>
        {months.map((month, index) => (
          <ScheduleFeedMonth 
            key={index}
            month={month}
            changeDayActive={changeDayActive}
          />
        ))}
      </div>
      }
      
      {timeslotsStore.monthSlots.length > 0 && (
        <ScheduleCalendar
          isOpen={calendarIsOpen}
          close={closeCalenar}
          day={selectedDay?.date}
          dateCalendarStart={new Date(timeslotsStore.monthSlots[0].date)}
        />
      )}
      <DoctorCard 
        timeslot={timeslot}
        doctor={doctorsStore.activeDoctor as TDoctor}
        selectSlot={selectSlot}
        nameIsText
        view={view === 'full' ? 'full' : 'short'}
      />
    </>
  )
})